import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { DocumentList } from "./document-center/DocumentList";
import { DocumentPreview } from "./document-center/DocumentPreview";

interface Document {
  id: string;
  name: string;
  size: number;
  type: string;
  path: string;
  created_at: string;
  user_id: string;
}

export const DocumentCenter = () => {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      console.log("Fetching documents...");
      
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        throw error;
      }

      console.log("Fetched documents:", data);
      setDocuments(data || []);
    } catch (err: any) {
      console.error('Error fetching documents:', err);
      setError(err.message);
      toast({
        title: "Error",
        description: "Failed to fetch documents. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (path: string, fileName: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(path);

      if (error) throw error;

      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      link.remove();

      toast({
        title: "Success",
        description: "File downloaded successfully!",
      });
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: "Failed to download file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string, path: string) => {
    try {
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([path]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .match({ id });

      if (dbError) throw dbError;

      setDocuments(prev => prev.filter(doc => doc.id !== id));
      toast({
        title: "Success",
        description: "Document deleted successfully!",
      });
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: "Error",
        description: "Failed to delete document. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePreview = async (path: string) => {
    try {
      const { data } = await supabase.storage
        .from('documents')
        .getPublicUrl(path);

      setPreviewUrl(data.publicUrl);
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: "Error",
        description: "Failed to preview document. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="p-8 bg-[#1A1F2C] dark:bg-[#0F1219] min-h-screen">
      <h2 className="text-2xl font-bold text-white mb-4">Document Center</h2>
      
      {isLoading ? (
        <div className="text-muted-foreground">Loading documents...</div>
      ) : error ? (
        <div className="text-red-500">Error: {error}</div>
      ) : documents.length === 0 ? (
        <div className="text-muted-foreground">No documents found.</div>
      ) : (
        <DocumentList
          documents={documents}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onDelete={handleDelete}
        />
      )}

      <DocumentPreview 
        url={previewUrl} 
        onClose={() => setPreviewUrl(null)} 
      />
    </div>
  );
};